import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InventoryCard from './InventoryCard';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { CATEGORIES } from '../lib/constants';

const { FiSearch, FiPlus, FiDownload, FiChevronDown, FiFileText } = FiIcons;

const InventoryGridView = ({ data, categoryKey, onDelete }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'inventory_id', direction: 'asc' });
  const [isExportOpen, setIsExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredData = useMemo(() => {
    let items = [...data];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      items = items.filter(item => 
        item.inventory_id?.toLowerCase().includes(lower) ||
        item.name?.toLowerCase().includes(lower) ||
        item.article_number?.toLowerCase().includes(lower) ||
        item.manufacturer?.toLowerCase().includes(lower)
      );
    }
    if (sortConfig.key) {
      items.sort((a, b) => {
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [data, searchTerm, sortConfig]);

  const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0);

  const handleExportExcel = () => {
    const formatted = filteredData.map(item => ({
      ID: item.inventory_id,
      Hersteller: item.manufacturer,
      Artikelnummer: item.article_number,
      Name: item.name,
      Wert: formatCurrency(item.total_value)
    }));
    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventar_Karten");
    XLSX.writeFile(wb, `MoBa_Export_Grid_${categoryKey}.xlsx`);
    setIsExportOpen(false);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const categoryLabel = CATEGORIES[categoryKey]?.label || categoryKey;
    doc.setFontSize(18);
    doc.text(`MoBa Inventar - ${categoryLabel} (Gefiltert)`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 14, 28);
    const tableColumn = ["ID", "Hersteller", "Art-Nr", "Bezeichnung", "Zustand", "Wert"];
    const tableRows = filteredData.map(item => [
      item.inventory_id,
      item.manufacturer,
      item.article_number,
      item.name || '-',
      item.condition || '-',
      formatCurrency(item.total_value)
    ]);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      theme: 'grid',
      headStyles: { fillColor: [234, 179, 8], textColor: [15, 23, 42] },
    });
    const finalY = doc.lastAutoTable.finalY + 10;
    const totalValue = filteredData.reduce((acc, curr) => acc + (curr.total_value || 0), 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Wert der Auswahl: ${formatCurrency(totalValue)}`, 14, finalY);
    doc.save(`MoBa_Grid_Export_${categoryKey}_${new Date().toISOString().slice(0, 10)}.pdf`);
    setIsExportOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="relative w-full lg:max-w-md">
            <input 
              type="text" 
              placeholder="Suchen..." 
              className="w-full pl-10 pr-4 py-3 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-yellow-500 text-sm outline-none transition-all bg-white dark:bg-slate-900 dark:text-white shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute left-3 top-3.5 text-slate-400">
              <SafeIcon icon={FiSearch} />
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="relative flex-1 sm:flex-none" ref={exportRef}>
              <button 
                onClick={() => setIsExportOpen(!isExportOpen)}
                className={`w-full flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-sm border ${isExportOpen ? 'bg-slate-800 dark:bg-yellow-500 text-white dark:text-slate-950 border-transparent' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50'}`}
              >
                <SafeIcon icon={FiDownload} /> Export
              </button>
              {isExportOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <button onClick={handleExportExcel} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-yellow-50 dark:hover:bg-yellow-500/10 dark:text-white flex items-center gap-2 transition-colors">
                    <SafeIcon icon={FiDownload} className="text-blue-500" /> Excel (.xlsx)
                  </button>
                  <button onClick={handleExportPDF} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-red-50 dark:hover:bg-red-500/10 dark:text-white flex items-center gap-2 border-t dark:border-slate-800 transition-colors">
                    <SafeIcon icon={FiFileText} className="text-red-500" /> PDF (.pdf)
                  </button>
                </div>
              )}
            </div>
            <button 
              onClick={() => navigate(`/inventory/${categoryKey}/new`)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-yellow-500 text-slate-900 rounded-xl font-black text-sm shadow-lg hover:bg-yellow-600 transition-all"
            >
              <SafeIcon icon={FiPlus} /> NEU
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Sortieren:</span>
          {[
            { key: 'inventory_id', label: 'ID' },
            { key: 'manufacturer', label: 'Marke' },
            { key: 'total_value', label: 'Wert' }
          ].map(s => (
            <button 
              key={s.key}
              onClick={() => setSortConfig({ key: s.key, direction: sortConfig.key === s.key && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all whitespace-nowrap border ${sortConfig.key === s.key ? 'bg-slate-800 dark:bg-yellow-500 text-white dark:text-slate-900 border-transparent' : 'bg-white dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-800'}`}
            >
              {s.label} {sortConfig.key === s.key && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </button>
          ))}
        </div>
      </div>

      {filteredData.length === 0 ? (
        <div className="py-20 text-center text-slate-400 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 text-xs font-bold uppercase tracking-widest">
          Keine Objekte gefunden.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {filteredData.map(item => (
            <InventoryCard key={item.id} item={item} categoryKey={categoryKey} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

export default InventoryGridView;